import { useState } from 'react';
import { useAsyncData } from 'shared/hooks/useAsyncData';
import {
  fetchArenaTournaments,
  promoteFromWaitlist as promoteFromWaitlistRequest,
  updateRegistrationStatus,
  updateTournamentStatus,
} from 'modules/arena/tournaments/tournamentsService';
import { ArenaTournament } from 'modules/arena/tournaments/types';

export function useArenaTournaments() {
  const { data: tournaments, isLoading, error, setData: setTournaments } = useAsyncData<ArenaTournament[]>(
    fetchArenaTournaments,
    []
  );
  const [selectedId, setSelectedId] = useState<string>('');

  const selectedTournament =
    tournaments.find((tournament) => tournament.id === selectedId) ?? tournaments[0] ?? null;

  function updateCategories(
    tournamentId: string,
    categoryId: string,
    updater: (category: ArenaTournament['categories'][number]) => ArenaTournament['categories'][number]
  ) {
    setTournaments((prev) =>
      prev.map((tournament) =>
        tournament.id !== tournamentId
          ? tournament
          : {
              ...tournament,
              categories: tournament.categories.map((category) =>
                category.id !== categoryId ? category : updater(category)
              ),
            }
      )
    );
  }

  function confirmRegistration(categoryId: string, registrationId: string) {
    if (!selectedTournament) return;
    updateCategories(selectedTournament.id, categoryId, (category) => ({
      ...category,
      registrations: category.registrations.map((registration) =>
        registration.id !== registrationId
          ? registration
          : { ...registration, status: 'confirmada', amountPaid: registration.amountDue }
      ),
    }));
    updateRegistrationStatus(registrationId, 'confirmada').catch(() => {});
  }

  function cancelRegistration(categoryId: string, registrationId: string) {
    if (!selectedTournament) return;
    updateCategories(selectedTournament.id, categoryId, (category) => ({
      ...category,
      registrations: category.registrations.map((registration) =>
        registration.id !== registrationId ? registration : { ...registration, status: 'cancelada' }
      ),
    }));
    updateRegistrationStatus(registrationId, 'cancelada').catch(() => {});
  }

  function promoteFromWaitlist(categoryId: string, waitlistId: string) {
    if (!selectedTournament) return;
    updateCategories(selectedTournament.id, categoryId, (category) => {
      const entry = category.waitlist.find((item) => item.id === waitlistId);
      if (!entry) return category;

      const activeCount = category.registrations.filter((r) => r.status !== 'cancelada').length;
      if (activeCount >= category.capacity) return category;

      return {
        ...category,
        waitlist: category.waitlist.filter((item) => item.id !== waitlistId),
        registrations: [
          ...category.registrations,
          {
            id: `reg-${entry.id}`,
            participants: entry.participants,
            mode: entry.mode,
            status: 'aguardando_pagamento',
            amountDue: 180,
            amountPaid: 0,
            registeredAtLabel: 'hoje (promovido da lista de espera)',
          },
        ],
      };
    });
    promoteFromWaitlistRequest(waitlistId).catch(() => {});
  }

  function publishTournament(id: string) {
    setTournaments((prev) =>
      prev.map((tournament) => (tournament.id !== id ? tournament : { ...tournament, status: 'publicado' }))
    );
    updateTournamentStatus(id, 'publicado').catch(() => {});
  }

  function closeTournament(id: string) {
    setTournaments((prev) =>
      prev.map((tournament) => (tournament.id !== id ? tournament : { ...tournament, status: 'encerrado' }))
    );
    updateTournamentStatus(id, 'encerrado').catch(() => {});
  }

  return {
    tournaments,
    isLoading,
    error,
    selectedId,
    setSelectedId,
    selectedTournament,
    confirmRegistration,
    cancelRegistration,
    promoteFromWaitlist,
    publishTournament,
    closeTournament,
  };
}
